import { useState, useEffect } from "react";
import "@/App.css";
import { ChevronRight, Lock, Server, ClipboardList, Smartphone, Mic, Brain, BarChart3, Check, X } from "lucide-react";

const ClueLogo = () => (
  <img 
    src="https://customer-assets.emergentagent.com/job_7129eee3-0b4e-42a6-9ad4-78ddf8d548dc/artifacts/6rzqbupk_Clue-logo.svg" 
    alt="Clue" 
    className="h-8 w-auto"
  />
);

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/90 backdrop-blur-lg shadow-sm' : 'bg-transparent'}`}>
      <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
        <ClueLogo />
        <div className="hidden md:flex items-center gap-8">
          <a href="#how" className="text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium">How It Works</a>
          <a href="#features" className="text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium">Features</a>
          <a href="#pricing" className="text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium">Pricing</a>
        </div>
        <a 
          href="https://wa.me/917358505328"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="nav-demo-btn"
          className="bg-slate-900 text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-slate-800 transition-all hover:scale-105"
        >
          Book Demo
        </a>
      </div>
    </nav>
  );
};

const IndustryTicker = () => {
  const industries = [
    { name: "Textile", emoji: "🧵" },
    { name: "Pharma", emoji: "💊" },
    { name: "FMCG", emoji: "🛒" },
    { name: "Steel", emoji: "🔩" },
    { name: "Jewellery", emoji: "💍" },
    { name: "Logistics", emoji: "🚚" },
    { name: "Auto Parts", emoji: "⚙️" },
    { name: "Agri", emoji: "🌾" },
  ];

  return (
    <div className="ticker-container overflow-hidden py-4 bg-lime-50 border-y border-lime-100">
      <div className="ticker-content flex animate-ticker">
        {[...industries, ...industries, ...industries, ...industries].map((ind, i) => (
          <span key={i} className="flex items-center gap-2 px-6 text-slate-600 whitespace-nowrap">
            <span className="text-lg">{ind.emoji}</span>
            <span className="font-medium">{ind.name}</span>
            <span className="text-lime-400 mx-4">·</span>
          </span>
        ))}
      </div>
    </div>
  );
};

const HeroSection = () => (
  <section className="pt-32 pb-16 px-6 relative overflow-hidden">
    {/* Dot pattern background */}
    <div className="absolute inset-0 dot-pattern opacity-40" />
    
    <div className="max-w-4xl mx-auto text-center relative z-10">
      {/* Live badge */}
      <div className="inline-flex items-center gap-2 bg-lime-100 text-slate-700 px-4 py-2 rounded-full text-sm font-medium mb-8 animate-fade-in">
        <span className="w-2 h-2 bg-lime-500 rounded-full animate-pulse" />
        Now Live for Indian Businesses
      </div>

      {/* Main heading */}
      <h1 className="text-5xl md:text-6xl lg:text-7xl font-semibold text-slate-900 leading-tight mb-6 animate-slide-up">
        Your Business Data,
        <br />
        <span className="relative inline-block">
          <span className="relative z-10">Answered in One Message</span>
          <span className="absolute bottom-2 left-0 right-0 h-4 bg-lime-200 -z-0 rounded" />
        </span>
      </h1>

      {/* Subtitle */}
      <p className="text-xl text-slate-600 max-w-2xl mx-auto mb-10 animate-slide-up-delay leading-relaxed">
        Ask your business anything — in plain English, over WhatsApp.
        Get instant answers from your own data. No IT team, no dashboards, no waiting.
      </p>

      {/* CTA buttons */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12 animate-slide-up-delay-2">
        <a 
          href="https://wa.me/917358505328"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="hero-demo-btn"
          className="inline-flex items-center justify-center gap-2 bg-slate-900 text-white px-8 py-4 rounded-full text-base font-medium hover:bg-slate-800 transition-all hover:scale-105 shadow-lg shadow-slate-900/20"
        >
          <span className="text-lime-400">🟢</span>
          Book Your Free Demo
          <ChevronRight className="w-4 h-4" />
        </a>
        <a 
          href="#how" 
          data-testid="hero-how-btn"
          className="inline-flex items-center justify-center gap-2 bg-white text-slate-700 px-8 py-4 rounded-full text-base font-medium border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-all"
        >
          See How It Works
        </a>
      </div>

      {/* Trust indicators */}
      <div className="flex flex-wrap justify-center gap-6 md:gap-10">
        <div className="flex items-center gap-2 text-slate-600">
          <Lock className="w-5 h-5 text-lime-600" />
          <span className="text-sm">Your data stays with you</span>
        </div>
        <div className="flex items-center gap-2 text-slate-600">
          <span className="text-lg">⚡</span>
          <span className="text-sm">Answer in under 5 seconds</span>
        </div>
        <div className="flex items-center gap-2 text-slate-600">
          <Smartphone className="w-5 h-5 text-lime-600" />
          <span className="text-sm">No new app to download</span>
        </div>
      </div>
    </div>
  </section>
);

const WhatsAppMockup = () => (
  <section className="py-16 px-6">
    <div className="max-w-xl mx-auto">
      <div className="bg-slate-900 rounded-3xl p-2 shadow-2xl shadow-slate-900/30">
        <div className="bg-[#0b141a] rounded-2xl overflow-hidden">
          {/* WhatsApp header */}
          <div className="bg-[#1f2c34] px-4 py-3 flex items-center gap-3">
            <div className="w-10 h-10 bg-lime-500 rounded-full flex items-center justify-center text-slate-900 font-bold text-sm">
              CL
            </div>
            <div>
              <div className="text-white font-medium text-sm">Clue Business AI</div>
              <div className="text-lime-400 text-xs flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-lime-400 rounded-full" />
                Online — Your data is ready
              </div>
            </div>
          </div>

          {/* Chat body */}
          <div className="p-4 space-y-3 min-h-[400px] bg-[url('https://web.whatsapp.com/img/bg-chat-tile-light_686b98c9fdffef3f63127759e2057750.png')] bg-repeat bg-[length:412px]">
            {/* User message */}
            <div className="flex justify-end">
              <div className="bg-[#005c4b] text-white px-4 py-2 rounded-2xl rounded-tr-md max-w-[80%] shadow">
                <p className="text-sm">How many meters of 40s cotton are pending dispatch to Mumbai buyers this week?</p>
                <p className="text-[10px] text-white/60 text-right mt-1">10:32 AM ✓✓</p>
              </div>
            </div>

            {/* Bot message */}
            <div className="flex justify-start animate-message-in">
              <div className="bg-[#202c33] text-white px-4 py-3 rounded-2xl rounded-tl-md max-w-[85%] shadow">
                <p className="font-medium text-sm mb-2">📦 Pending Dispatch — Mumbai Buyers</p>
                <p className="text-lime-400 text-lg font-semibold mb-2">3,840 meters <span className="text-white/60 text-sm font-normal">pending across 6 buyers:</span></p>
                <div className="text-sm space-y-1 mb-3">
                  <p>• Mehta Fabrics — <span className="text-lime-400">1,200m</span></p>
                  <p>• Shah Textiles — <span className="text-lime-400">960m</span></p>
                  <p>• Patel Garments — <span className="text-lime-400">740m</span></p>
                  <p className="text-white/60">• +3 more buyers</p>
                </div>
                <p className="text-sm">Most urgent: Mehta Fabrics — delivery is <span className="text-red-400">2 days overdue</span> ⚠️</p>
                <p className="text-[10px] text-white/60 text-right mt-2">10:32 AM</p>
              </div>
            </div>

            {/* User follow-up */}
            <div className="flex justify-end animate-message-in-delay">
              <div className="bg-[#005c4b] text-white px-4 py-2 rounded-2xl rounded-tr-md max-w-[80%] shadow">
                <p className="text-sm">Send me a chart</p>
                <p className="text-[10px] text-white/60 text-right mt-1">10:33 AM ✓✓</p>
              </div>
            </div>

            {/* Bot chart response */}
            <div className="flex justify-start animate-message-in-delay-2">
              <div className="bg-[#202c33] text-white px-4 py-3 rounded-2xl rounded-tl-md max-w-[85%] shadow">
                <p className="text-sm">📊 Bar chart generated ✅</p>
                <p className="text-[10px] text-white/40 mt-1">dispatch_mumbai_feb28.png</p>
                <p className="text-[10px] text-white/60 text-right mt-2">10:33 AM</p>
              </div>
            </div>
          </div>

          {/* Input bar */}
          <div className="bg-[#1f2c34] px-4 py-3 flex items-center gap-3">
            <div className="flex-1 bg-[#2a3942] rounded-full px-4 py-2 text-white/50 text-sm">
              🎤 Send a voice note or type...
            </div>
            <div className="w-10 h-10 bg-lime-500 rounded-full flex items-center justify-center">
              <Mic className="w-5 h-5 text-slate-900" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const HowItWorks = () => {
  const steps = [
    {
      num: "01",
      icon: <Mic className="w-8 h-8" />,
      title: "Ask in Plain Language",
      desc: "Send a voice note or type your question on WhatsApp — in English or your regional language. Ask it exactly like you'd ask your manager.",
      note: "Works with voice, text, or a mix of both"
    },
    {
      num: "02",
      icon: <Brain className="w-8 h-8" />,
      title: "AI Finds the Answer",
      desc: "Our AI understands your question, pulls the right data from your business database, and prepares a clear, accurate response — in under 5 seconds.",
      note: "Works with Tally, ERPNext, or your existing business software"
    },
    {
      num: "03",
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Get a Clear Answer",
      desc: "Receive a plain-language summary, a table, or a chart image — delivered right back to your WhatsApp. Even listen to it while you drive.",
      note: "Text summary, table, or chart — your choice"
    }
  ];

  return (
    <section id="how" className="py-24 px-6 bg-slate-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <span className="inline-block bg-lime-100 text-lime-700 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
            How It Works
          </span>
          <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 mb-4">
            Intelligence in
            <br />
            <span className="text-lime-600">3 Simple Steps</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-lg mx-auto">
            No IT setup. No new software. Just WhatsApp — the app already in your pocket.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {steps.map((step, i) => (
            <div 
              key={i} 
              className="bg-white rounded-2xl p-8 border border-slate-100 hover:border-lime-200 hover:shadow-lg transition-all duration-300 group"
              data-testid={`how-step-${i + 1}`}
            >
              <div className="flex items-center gap-4 mb-6">
                <span className="text-5xl font-bold text-slate-100 group-hover:text-lime-100 transition-colors">{step.num}</span>
                <div className="w-14 h-14 bg-lime-100 rounded-xl flex items-center justify-center text-lime-600 group-hover:bg-lime-500 group-hover:text-white transition-all">
                  {step.icon}
                </div>
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">{step.title}</h3>
              <p className="text-slate-600 mb-4 leading-relaxed">{step.desc}</p>
              <p className="text-sm text-lime-600 bg-lime-50 px-3 py-2 rounded-lg inline-block">{step.note}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const RealExamples = () => {
  const questions = [
    {
      industry: "🧵 Textile — Surat / Tirupur",
      question: "How many meters of 40s cotton are pending dispatch to Mumbai buyers this week?"
    },
    {
      industry: "💊 Pharma — Nagpur / Baddi",
      question: "Which medicines in my godown expire in the next 60 days and which stockist hasn't cleared them?"
    },
    {
      industry: "🛒 FMCG — Patna / Kanpur",
      question: "Which retailers in my beat haven't placed an order in the last 3 weeks?"
    },
    {
      industry: "🔩 Steel — Mandi Gobindgarh",
      question: "What was my margin on the last 10 TMT deals and which customer squeezed it the most?"
    },
    {
      industry: "💍 Jewellery — Zaveri Bazaar / Thrissur",
      question: "How much gold is currently with my karigars and who has been holding it the longest?"
    },
    {
      industry: "🚚 Logistics — Nagpur / Surat",
      question: "Which vehicles ran below 70% utilisation this week and on which routes?"
    }
  ];

  return (
    <section className="py-24 px-6 bg-slate-900">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <span className="inline-block bg-lime-500/20 text-lime-400 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
            Real Examples
          </span>
          <h2 className="text-4xl md:text-5xl font-semibold text-white mb-4">
            Questions Business Owners
            <br />
            <span className="text-lime-400">Ask Every Day</span>
          </h2>
          <p className="text-lg text-slate-400 max-w-xl mx-auto">
            These are real questions from owners just like you — across every major Indian trade category.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {questions.map((q, i) => (
            <div 
              key={i} 
              className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-2xl p-6 hover:border-lime-500/50 transition-all duration-300 hover:transform hover:-translate-y-1"
              data-testid={`example-${i + 1}`}
            >
              <p className="text-lime-400 text-sm font-medium mb-3">{q.industry}</p>
              <p className="text-white text-lg leading-relaxed">{q.question}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const WhoIsItFor = () => {
  const audiences = [
    { icon: "🧵", name: "Textile Trader", cities: "Surat · Tirupur · Ludhiana" },
    { icon: "💊", name: "Pharma Stockist", cities: "Bhagirath Palace · Baddi · Nagpur" },
    { icon: "🛒", name: "FMCG Distributor", cities: "Patna · Indore · Agra" },
    { icon: "🔩", name: "Steel Trader", cities: "Mandi Gobindgarh · Raipur" },
    { icon: "💍", name: "Jewellery Maker", cities: "Zaveri Bazaar · Thrissur · T Nagar" },
    { icon: "🌾", name: "Agri Trader", cities: "Indore · Guntur · Unjha" }
  ];

  return (
    <section id="features" className="py-24 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <span className="inline-block bg-lime-100 text-lime-700 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
            Who It's Built For
          </span>
          <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 mb-4">
            Built for India's
            <br />
            <span className="text-lime-600">Business Owners</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-lg mx-auto">
            For the people who run on data every day — but don't have an IT team to pull it for them.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {audiences.map((aud, i) => (
            <div 
              key={i} 
              className="bg-white border border-slate-100 rounded-2xl p-6 text-center hover:border-lime-200 hover:shadow-lg transition-all duration-300 group"
              data-testid={`audience-${i + 1}`}
            >
              <div className="text-4xl mb-3 group-hover:scale-110 transition-transform">{aud.icon}</div>
              <h3 className="font-semibold text-slate-900 mb-1">{aud.name}</h3>
              <p className="text-xs text-slate-500">{aud.cities}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const WhyDifferent = () => (
  <section className="py-24 px-6 bg-slate-900">
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-16">
        <span className="inline-block bg-lime-500/20 text-lime-400 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
          Why Clue
        </span>
        <h2 className="text-4xl md:text-5xl font-semibold text-white mb-4">
          Completely Different
          <br />
          <span className="text-lime-400">From Every Other Tool</span>
        </h2>
        <p className="text-lg text-slate-400 max-w-lg mx-auto">
          No new app to learn. No data migration. No IT dependency.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Other tools */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8" data-testid="comparison-others">
          <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
            <X className="w-6 h-6 text-red-400" />
            Every Other Tool
          </h3>
          <ul className="space-y-4">
            {[
              "Requires learning new software",
              "You must migrate your data first",
              "Needs an IT person or accountant",
              "English dashboards only",
              "Stuck to your office desk",
              "Expensive enterprise pricing"
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-slate-300">
                <X className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        {/* Clue */}
        <div className="bg-gradient-to-br from-lime-500 to-lime-600 rounded-2xl p-8" data-testid="comparison-clue">
          <h3 className="text-xl font-semibold text-slate-900 mb-6 flex items-center gap-2">
            <Check className="w-6 h-6" />
            Clue
          </h3>
          <ul className="space-y-4">
            {[
              "Just WhatsApp — already in your hand",
              "Connects to your existing Tally or ERP",
              "Ask yourself, get the answer yourself",
              "Works in Hindi, Tamil, Gujarati and more",
              "Use it while driving — voice answers",
              "Priced for Indian SMBs"
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-slate-900">
                <Check className="w-5 h-5 shrink-0 mt-0.5" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </section>
);

const SecuritySection = () => {
  const features = [
    {
      icon: <Lock className="w-6 h-6" />,
      title: "Read-Only Access",
      desc: "We only read your data. Nothing is ever deleted or changed. Not now, not ever."
    },
    {
      icon: <Server className="w-6 h-6" />,
      title: "Data Stays on Your Server",
      desc: "Your data remains in your Tally or server. We only fetch the answer to your question — nothing more."
    },
    {
      icon: <ClipboardList className="w-6 h-6" />,
      title: "Full Audit Log",
      desc: "Every query is recorded — who asked what and when. You stay in full control of your team's access."
    },
    {
      icon: <Smartphone className="w-6 h-6" />,
      title: "Registered Numbers Only",
      desc: "Only your approved WhatsApp numbers can access your data. No outsider can query your business."
    }
  ];

  return (
    <section className="py-24 px-6 bg-slate-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <span className="inline-block bg-lime-100 text-lime-700 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
            Data Security
          </span>
          <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 mb-4">
            Your Data Stays
            <br />
            <span className="text-lime-600">Yours. Always.</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-xl mx-auto">
            We know data privacy is non-negotiable for Indian business owners. Security was built in from day one — not bolted on later.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feat, i) => (
            <div 
              key={i} 
              className="bg-white border border-slate-100 rounded-2xl p-6 hover:border-lime-200 hover:shadow-lg transition-all duration-300"
              data-testid={`security-${i + 1}`}
            >
              <div className="w-12 h-12 bg-lime-100 rounded-xl flex items-center justify-center text-lime-600 mb-4">
                {feat.icon}
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">{feat.title}</h3>
              <p className="text-slate-600 text-sm leading-relaxed">{feat.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const PricingSection = () => {
  return (
    <section id="pricing" className="py-24 px-6 bg-slate-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <span className="inline-block bg-lime-100 text-lime-700 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
            Pricing
          </span>
          <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 mb-4">
            Pay for answers.
            <br />
            <span className="text-lime-600">Not for software.</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-xl mx-auto">
            Most business tools charge you whether you use them or not. Clue charges you only when it saves you time.
          </p>
        </div>

        {/* Pricing Cards */}
        <div id="cta" className="grid md:grid-cols-3 gap-6 mb-8">
          {/* Starter */}
          <div className="bg-white border border-slate-200 rounded-2xl p-8 hover:shadow-lg transition-all" data-testid="pricing-starter">
            <div className="mb-6">
              <span className="text-2xl mb-2 block">🟢</span>
              <h3 className="text-xl font-semibold text-slate-900">Starter</h3>
              <div className="mt-4">
                <span className="text-4xl font-bold text-slate-900">₹1,999</span>
                <span className="text-slate-500">/month</span>
              </div>
            </div>
            <p className="text-lime-600 font-medium text-sm mb-4">For owners who want to try before they commit</p>
            <p className="text-slate-600 text-sm leading-relaxed mb-6">
              200 questions a month. That's enough to check your receivables, track your top buyers, and stop calling your accountant for numbers — every single day. Cancel anytime.
            </p>
            <a 
              href="https://wa.me/917358505328"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full bg-slate-100 text-slate-700 py-3 rounded-full font-medium hover:bg-slate-200 transition-all flex items-center justify-center gap-2"
            >
              Get Started
            </a>
          </div>

          {/* Standard - Featured */}
          <div className="bg-slate-900 border-2 border-lime-400 rounded-2xl p-8 relative transform md:-translate-y-4 shadow-2xl" data-testid="pricing-standard">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-lime-400 text-slate-900 text-xs font-bold px-4 py-1.5 rounded-full">
              MOST POPULAR
            </div>
            <div className="mb-6">
              <span className="text-2xl mb-2 block">🔵</span>
              <h3 className="text-xl font-semibold text-white">Standard</h3>
              <div className="mt-4">
                <span className="text-4xl font-bold text-white">₹3,999</span>
                <span className="text-slate-400">/month</span>
              </div>
            </div>
            <p className="text-lime-400 font-medium text-sm mb-4">For owners who run data-heavy businesses</p>
            <p className="text-slate-300 text-sm leading-relaxed mb-6">
              600 questions a month. Built for trading and manufacturing owners who need real-time answers on inventory, margins, dispatch, and collections — without waiting for end-of-day reports.
            </p>
            <a 
              href="https://wa.me/917358505328"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="pricing-cta-btn"
              className="w-full bg-lime-400 text-slate-900 py-3 rounded-full font-semibold hover:bg-lime-300 transition-all flex items-center justify-center gap-2"
            >
              Get Started
            </a>
          </div>

          {/* Enterprise */}
          <div className="bg-white border border-slate-200 rounded-2xl p-8 hover:shadow-lg transition-all" data-testid="pricing-enterprise">
            <div className="mb-6">
              <span className="text-2xl mb-2 block">🏢</span>
              <h3 className="text-xl font-semibold text-slate-900">Enterprise</h3>
              <div className="mt-4">
                <span className="text-4xl font-bold text-slate-900">Let's talk</span>
              </div>
            </div>
            <p className="text-lime-600 font-medium text-sm mb-4">For larger operations, group companies, or CAs</p>
            <p className="text-slate-600 text-sm leading-relaxed mb-6">
              Custom pricing. Multi-user access. White-label options. Perfect for managing multiple clients or scaling across your organization.
            </p>
            <a 
              href="https://wa.me/917358505328"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full bg-slate-100 text-slate-700 py-3 rounded-full font-medium hover:bg-slate-200 transition-all flex items-center justify-center gap-2"
            >
              Book a call →
            </a>
          </div>
        </div>

        {/* Overage Info */}
        <div className="bg-slate-100 border border-slate-200 rounded-2xl p-6 mb-12 max-w-2xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <span className="text-xl">⚡</span>
            <span className="font-semibold text-slate-900">Need more? Pay ₹8 per extra question.</span>
          </div>
          <p className="text-slate-600 text-sm">
            Use more, pay more. Simple. Heavy month? Just recharge. No contract changes, no plan upgrades. Clue scales with your business automatically.
          </p>
        </div>

        {/* Main CTA */}
        <div className="text-center">
          <a 
            href="https://wa.me/917358505328"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-lime-500 text-slate-900 px-10 py-4 rounded-full text-lg font-semibold hover:bg-lime-400 transition-all hover:scale-105 shadow-lg shadow-lime-500/20"
          >
            <span>🟢</span>
            Try free on WhatsApp
          </a>
          <p className="text-slate-500 text-sm mt-4">Your accountant is offline. Clue isn't.</p>
        </div>
      </div>
    </section>
  );
};

const Footer = () => (
  <footer className="py-12 px-6 bg-slate-900 border-t border-slate-800">
    <div className="max-w-6xl mx-auto">
      <div className="flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-lime-500 rounded-lg flex items-center justify-center text-slate-900 font-bold text-sm">
            CL
          </div>
          <span className="text-white font-semibold">Clue</span>
        </div>
        <p className="text-slate-500 text-sm text-center">
          © 2026 Clue. Built for Indian businesses. All rights reserved.
        </p>
        <div className="flex items-center gap-6">
          <a href="#" className="text-slate-400 hover:text-white text-sm transition-colors">Privacy</a>
          <a href="#" className="text-slate-400 hover:text-white text-sm transition-colors">Terms</a>
          <a href="#" className="text-slate-400 hover:text-white text-sm transition-colors">Contact</a>
        </div>
      </div>
    </div>
  </footer>
);

function App() {
  // Hide Emergent badge
  useEffect(() => {
    const hideBadge = () => {
      document.querySelectorAll('a, div, span').forEach(el => {
        if (el.innerText && el.innerText.includes('Made with Emergent')) {
          let parent = el;
          while (parent && parent.tagName !== 'BODY') {
            parent.style.cssText = 'display:none!important;visibility:hidden!important;opacity:0!important;';
            parent = parent.parentElement;
            if (parent && parent.style && parent.style.position === 'fixed') {
              parent.style.cssText = 'display:none!important;visibility:hidden!important;opacity:0!important;';
              break;
            }
          }
        }
      });
    };
    hideBadge();
    const observer = new MutationObserver(hideBadge);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return (
    <div className="App bg-white min-h-screen">
      <Navbar />
      <HeroSection />
      <IndustryTicker />
      <WhatsAppMockup />
      <HowItWorks />
      <RealExamples />
      <WhoIsItFor />
      <WhyDifferent />
      <SecuritySection />
      <PricingSection />
      <Footer />
    </div>
  );
}

export default App;
